// applications/dossier hooks — the review + consent + submit screen.
import { useCallback, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toLocale } from "@core/locale";
import { isApiError } from "@core/errors";
import { applicationKeys } from "@modules/applications/_shared/keys";
import { conflictRevision } from "./useDossierAutosave";
import { getApplication, putConsent, submitApplication } from "../services";
import type { Application } from "../types";

/** Mirrors `DocumentMutationResult`'s shape (`useDossierAutosave`) — one message, not per-field. */
export type ConsentMutationResult =
  { ok: true } | { ok: false; message: string };

export type SubmitResult = { ok: true } | { ok: false; message: string };

/**
 * The review screen's own mutations — consent toggles and the final submit.
 *
 * Deliberately separate from `useDossierAutosave`: nothing here is a per-field autosave queue
 * (consent is one explicit toggle at a time, submit fires once), so the pending-jobs machinery
 * that hook owns would be pure overhead. Both hooks read and write the SAME cache entry
 * (`applicationKeys.detail`), so a consent accepted here and a field edited on the wizard tabs
 * stay consistent without either hook knowing the other exists.
 */
export function useDossierReview(applicationId: string) {
  const queryClient = useQueryClient();
  const { t, i18n: instance } = useTranslation("acquire");
  const locale = toLocale(instance.resolvedLanguage ?? "fr");
  // `applicationKeys.detail` returns a fresh array literal on every call — memoized so the
  // callbacks below that depend on it don't get a new "stable" reference every render (the
  // React Compiler flags an unstable dependency here as unsafe to memoize around otherwise).
  const key = useMemo(
    () => applicationKeys.detail(applicationId, locale),
    [applicationId, locale]
  );

  const [pendingConsentType, setPendingConsentType] = useState<string | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const refetchApplication = useCallback(async () => {
    const fresh = await getApplication(applicationId, locale);
    queryClient.setQueryData(key, fresh);
    return fresh;
  }, [applicationId, locale, queryClient, key]);

  const toggleConsent = useCallback(
    async (
      consentType: string,
      accepted: boolean
    ): Promise<ConsentMutationResult> => {
      setPendingConsentType(consentType);
      try {
        for (let attempt = 0; attempt < 2; attempt += 1) {
          const revision =
            queryClient.getQueryData<Application>(key)?.revision ?? 0;
          try {
            const updated = await putConsent(
              applicationId,
              consentType,
              revision,
              accepted,
              locale
            );
            queryClient.setQueryData(key, updated);
            return { ok: true };
          } catch (error) {
            const fresh = conflictRevision(error);
            if (fresh !== null && attempt === 0) {
              await refetchApplication();
              continue;
            }
            return {
              ok: false,
              message: isApiError(error)
                ? t("dossier.review.consent-failed")
                : t("dossier.review.consent-failed"),
            };
          }
        }
        return { ok: false, message: t("dossier.review.consent-failed") };
      } finally {
        setPendingConsentType(null);
      }
    },
    [applicationId, locale, queryClient, key, refetchApplication, t]
  );

  const submit = useCallback(async (): Promise<SubmitResult> => {
    setIsSubmitting(true);
    try {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        const revision =
          queryClient.getQueryData<Application>(key)?.revision ?? 0;
        try {
          const updated = await submitApplication(applicationId, revision);
          queryClient.setQueryData(key, updated);
          return { ok: true };
        } catch (error) {
          const fresh = conflictRevision(error);
          if (fresh !== null && attempt === 0) {
            await refetchApplication();
            continue;
          }
          if (isApiError(error) && error.code === "application_incomplete") {
            // The review screen already renders `missing_items` from the SAME cached
            // application — re-set from the error's own copy isn't needed, a refetch confirms
            // the server's current view so the screen reflects exactly what blocked it.
            await refetchApplication();
            return {
              ok: false,
              message: t("dossier.review.submit-incomplete"),
            };
          }
          if (
            isApiError(error) &&
            error.code === "email_verification_required"
          ) {
            return {
              ok: false,
              message: t("dossier.review.submit-email-unverified"),
            };
          }
          return { ok: false, message: t("dossier.review.submit-failed") };
        }
      }
      return { ok: false, message: t("dossier.review.submit-failed") };
    } finally {
      setIsSubmitting(false);
    }
  }, [applicationId, queryClient, key, refetchApplication, t]);

  return { toggleConsent, pendingConsentType, submit, isSubmitting };
}
